import Tooltip from '@mui/material/Tooltip';
import { FC, useState, ComponentType, memo, useMemo, useCallback, SyntheticEvent } from 'react';
import { formatTimestamp } from '../../utils/dateUtils';
import { checkOverflow } from '../../utils/utilFunctions';
import * as Styled from './style';
import { To } from 'react-router-dom';

interface CellValues {
	field?: string;
	id?: string | number;
	formattedValue?: string | number;
}

interface IconComponentProps {
	status?: string;
}

interface BasicCellTextProps {
	cellValues: CellValues;
	StartIcon?: ComponentType<IconComponentProps>;
	iconStatus?: string;
	isDate?: boolean;
	to?: To;
	disabled?: boolean;
}

const BasicCellText: FC<BasicCellTextProps> = memo(
	({ cellValues, StartIcon, iconStatus, to, isDate = false, disabled = false }) => {
		const [isOpen, setIsOpen] = useState(false);

		const formattedText = useMemo(
			() =>
				isDate ? formatTimestamp(cellValues?.formattedValue as string) : cellValues?.formattedValue,
			[cellValues?.formattedValue, isDate]
		);

		const handleOpen = useCallback((event: SyntheticEvent) => {
			checkOverflow(event) && setIsOpen(true);
		}, []);

		const handleClose = useCallback(() => setIsOpen(false), []);

		const testIdContainer = `cellContainer-${cellValues?.field}-${cellValues?.id}`;
		const testIdInfo = `rowInfo-${cellValues?.field}-${cellValues?.id}`;
		const testIdLink = `rowLink-${cellValues?.field}-${cellValues?.id}`;

		return (
			<Tooltip
				open={isOpen}
				onOpen={handleOpen}
				onClose={handleClose}
				arrow
				placement="top"
				title={formattedText}
			>
				<Styled.CellContainer data-testid={testIdContainer}>
					{StartIcon && cellValues?.formattedValue && <StartIcon status={iconStatus} />}
					{to ? (
						<Styled.CardRowInfoLink
							to={to}
							width="100%"
							data-testid={testIdLink}
							disabled={disabled}
						>
							{formattedText}
						</Styled.CardRowInfoLink>
					) : (
						<Styled.CardRowInfo data-testid={testIdInfo} disabled={disabled}>
							{formattedText}
						</Styled.CardRowInfo>
					)}
				</Styled.CellContainer>
			</Tooltip>
		);
	}
);

BasicCellText.displayName = 'BasicCellText';

export default BasicCellText;
